import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Chip, IconButton, MenuItem, Paper, TablePagination, TableSortLabel, TextField } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { StyledTableCell } from '../../common/components/styled/StyledTable';
import { GoodsStatusSR } from '../../common/Types';
import { formatNumber } from '../../common/Extensions';
import { FormatDateText } from '../../common/datesHelper';
import { useAppSelector } from '../../hooks/hooks';
import { canSeeValuableInfo } from '../../redux/selectors/employeeSelector';
import { getColorForGoodsStatus } from './GoodsHelper';
import { InvoiceSortField, InvoiceSummary, SortDirection, sortInvoices } from './InvoiceHelper';
import {
    MobileCardRow,
    mobileHiddenHeadSx,
    mobileTableSx,
    useIsMobile,
} from '../../common/components/mobile/MobilePrimitives';

/** Tekstualne kolone se prvi put sortiraju A→Ž, datumi i brojevi od najvećeg. */
const DEFAULT_DIRECTION: Record<InvoiceSortField, SortDirection> = {
    invoiceNumber: 'asc',
    supplierName: 'asc',
    enteredDate: 'desc',
    arrivalDate: 'desc',
    articlesCount: 'desc',
    totalValue: 'desc',
};

const SORT_LABELS: Record<InvoiceSortField, string> = {
    invoiceNumber: 'Br. fakture',
    supplierName: 'Dobavljač',
    enteredDate: 'Datum unosa',
    arrivalDate: 'Datum prijema',
    articlesCount: 'Br. artikala',
    totalValue: 'Vrednost',
};

const sortLabelSx = { '&.Mui-active, &:hover': { color: 'text.primary' } } as const;

type InvoicesTableProps = {
    invoices: InvoiceSummary[];
    showSupplier: boolean;
    onSelectInvoice: (invoice: InvoiceSummary) => void;
};

export default function InvoicesTable(props: InvoicesTableProps) {
    const { invoices, showSupplier, onSelectInvoice } = props;

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [sortField, setSortField] = React.useState<InvoiceSortField>('enteredDate');
    const [sortDirection, setSortDirection] = React.useState<SortDirection>('desc');

    const canSeeMoney = useAppSelector(canSeeValuableInfo);
    const currency = useAppSelector((state) => state.companyDetails.companyDetails?.currency);

    const isMobile = useIsMobile();

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSort = (field: InvoiceSortField) => {
        if (field === sortField) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection(DEFAULT_DIRECTION[field]);
        }
        setPage(0);
    };

    // Bez kolone dobavljača nema ni sortiranja po njoj.
    const effectiveSortField = !showSupplier && sortField === 'supplierName' ? 'enteredDate' : sortField;

    const sortedInvoices = React.useMemo(
        () => sortInvoices(invoices, effectiveSortField, sortDirection),
        [invoices, effectiveSortField, sortDirection],
    );

    React.useEffect(() => {
        if (invoices.length <= page * rowsPerPage) {
            setPage(0);
        }
    }, [invoices, page]);

    const visibleRows = React.useMemo(
        () => sortedInvoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedInvoices, page, rowsPerPage],
    );

    const sortableFields = (Object.keys(SORT_LABELS) as InvoiceSortField[]).filter(
        (f) => (f !== 'supplierName' || showSupplier) && (f !== 'totalValue' || canSeeMoney),
    );

    const sortHeader = (field: InvoiceSortField, align: 'left' | 'center' | 'right', label?: string) => (
        <StyledTableCell align={align} sortDirection={effectiveSortField === field ? sortDirection : false}>
            <TableSortLabel
                active={effectiveSortField === field}
                direction={effectiveSortField === field ? sortDirection : DEFAULT_DIRECTION[field]}
                onClick={() => handleSort(field)}
                sx={sortLabelSx}
            >
                {label ?? SORT_LABELS[field]}
            </TableSortLabel>
        </StyledTableCell>
    );

    const statusChips = (invoice: InvoiceSummary) =>
        invoice.statuses.map((s) => (
            <Chip key={s} size="small" variant="outlined" color={getColorForGoodsStatus(s)} label={GoodsStatusSR[s]} />
        ));

    const valueText = (invoice: InvoiceSummary) =>
        invoice.totalValue > 0 ? formatNumber(Math.round(invoice.totalValue * 100) / 100) : '/';

    const dateText = (date?: Date) => (date ? FormatDateText(date) : '/');

    const columnsCount = 5 + (showSupplier ? 1 : 0) + (canSeeMoney ? 1 : 0);

    return (
        <Box sx={{ width: '100%' }}>
            {isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TextField
                        select
                        size="small"
                        label="Sortiraj po"
                        value={effectiveSortField}
                        onChange={(e) => handleSort(e.target.value as InvoiceSortField)}
                        sx={{ flex: 1 }}
                    >
                        {sortableFields.map((f) => (
                            <MenuItem key={f} value={f}>
                                {SORT_LABELS[f]}
                            </MenuItem>
                        ))}
                    </TextField>
                    <IconButton
                        aria-label="smer sortiranja"
                        onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                    >
                        {sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                    </IconButton>
                </Box>
            )}
            <Paper sx={{ width: '100%', mb: 2 }}>
                <TableContainer sx={{ margin: 'auto' }}>
                    <Table sx={mobileTableSx(700)} size="small" stickyHeader aria-label="fakture">
                        <TableHead sx={mobileHiddenHeadSx}>
                            <TableRow>
                                {sortHeader('invoiceNumber', 'left')}
                                {showSupplier && sortHeader('supplierName', 'center')}
                                {sortHeader('enteredDate', 'center')}
                                {sortHeader('arrivalDate', 'center')}
                                {sortHeader('articlesCount', 'center')}
                                <StyledTableCell align="center">Status robe</StyledTableCell>
                                {canSeeMoney &&
                                    sortHeader(
                                        'totalValue',
                                        'right',
                                        'Vrednost' + (currency ? ' (' + currency + ')' : ''),
                                    )}
                                <StyledTableCell sx={{ width: 50 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows.map((invoice) => {
                                if (isMobile) {
                                    return (
                                        <MobileCardRow
                                            key={invoice.key}
                                            title={invoice.invoiceNumber}
                                            subtitle={showSupplier ? invoice.supplierName : undefined}
                                            header={statusChips(invoice)}
                                            actions={<ChevronRightIcon color="action" sx={{ mt: 1, mr: 1 }} />}
                                            fields={[
                                                { label: 'Datum unosa', value: dateText(invoice.enteredDate) },
                                                { label: 'Datum prijema', value: dateText(invoice.arrivalDate) },
                                                { label: 'Br. artikala', value: invoice.articlesCount },
                                                ...(canSeeMoney
                                                    ? [
                                                          {
                                                              label: 'Vrednost',
                                                              value:
                                                                  valueText(invoice) +
                                                                  (currency && invoice.totalValue > 0
                                                                      ? ' ' + currency
                                                                      : ''),
                                                          },
                                                      ]
                                                    : []),
                                            ]}
                                            onClick={() => onSelectInvoice(invoice)}
                                        />
                                    );
                                }
                                return (
                                    <TableRow
                                        key={invoice.key}
                                        hover
                                        onClick={() => onSelectInvoice(invoice)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                                            {invoice.invoiceNumber}
                                        </TableCell>
                                        {showSupplier && (
                                            <TableCell align="center">{invoice.supplierName ?? '/'}</TableCell>
                                        )}
                                        <TableCell align="center">{dateText(invoice.enteredDate)}</TableCell>
                                        <TableCell align="center">{dateText(invoice.arrivalDate)}</TableCell>
                                        <TableCell align="center">{invoice.articlesCount}</TableCell>
                                        <TableCell align="center">
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: 0.5,
                                                    flexWrap: 'wrap',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {statusChips(invoice)}
                                            </Box>
                                        </TableCell>
                                        {canSeeMoney && <TableCell align="right">{valueText(invoice)}</TableCell>}
                                        <TableCell align="center" sx={{ py: 0 }}>
                                            <ChevronRightIcon color="action" />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {invoices.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={columnsCount + 1} sx={{ color: 'text.secondary' }}>
                                        Nema faktura za zadate filtere.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={invoices.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
}
